// Copyright (c) 2019 by Audere
// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import React, { MouseEvent, useEffect, useState } from "react";

import { getApi } from "../store/api";
import "./LoginPage.css";
import { URLS } from "../urls";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "./ErrorBoundary";
import { Setter, scrollIntoViewSoon } from "../UiUtil";

export const LoginPageLink: React.FC<{}> = () => {
  const [gotStuck, setGotStuck_] = useState(false);
  const [didntGetEmail, setDidntGetEmail] = useState(false);
  const [isLinkSafe, setIsLinkSafe_] = useState(false);

  function onlyOne(setters: Setter<boolean>[]): Setter<boolean>[] {
    return setters.map((_, index) =>
      (value: boolean) => setters.forEach((s, i) => s(value && i === index))
    );
  }
  const [setGotStuck, setIsLinkSafe] = onlyOne([setGotStuck_, setIsLinkSafe_]);

  function reveal(setter: Setter<boolean>) {
    return ({ currentTarget: elem }: MouseEvent<HTMLButtonElement>) => {
      setter(true);
      scrollIntoViewSoon(elem);
    };
  }

  useEffect(() => attachAuthUI(true));
  return (
    <ErrorBoundary>
      <div className="content">
        <div>
          <h1>Volunteer Reporting Login</h1>
        </div>

        <div id="firebaseui-auth-container" />

        <div className="explanation">
          <div className="section">
            <h3>Notice</h3>
          </div>
          <div className="section">
            Entering an email address and clicking "NEXT" will send you a one-time
            link to log in.
          </div>
          <div className="section">
            It might take a few minutes to get to you.
            If you still don't see it, please also check your Spam / Junk Mail folder.
          </div>
          <div className="section">
            <b>IMPORTANT:</b> We have migrated volunteer hours here from the old
            system.
            Please login with the same email address to include those activities
            in your account.
          </div>
          <div className="section">
            <button
              type="button"
              onClick={reveal(setGotStuck)}
              disabled={gotStuck}
            >
              Help! I'm stuck.
            </button>
          </div>
          {gotStuck && (
            <>
              <div className="section">
                Did you:
                <ul>
                  <li>Enter your email address correctly above?</li>
                  <li>Click "NEXT"?</li>
                  <li>Wait 5 minutes and check your email afterwards?</li>
                  <li>Check your Spam / Junk Mail folder?</li>
                  <li>Double-check that your email is typed correctly?</li>
                </ul>
              </div>
              <div className="section">
                <button
                  type="button"
                  onClick={reveal(setDidntGetEmail)}
                  disabled={didntGetEmail}
                >
                  Yes, I did <i>all</i> of those and I <i>still</i> did not get a login email.
                </button>
              </div>
              {didntGetEmail && (
                <div className="section">
                  If you get stuck here and are unable to log in, send an
                  email to mmarucheck@gmail.com:
                      <ul>
                    <li>
                      The email needs to mention "volunteer-rep login" in
                      the subject.
                        </li>
                    <li>
                      The email needs to come from the same email account
                      you want to use to log in here.
                        </li>
                    <li>
                      The email needs to explain what you tried and what is
                      not working.
                        </li>
                  </ul>
                </div>
              )}
            </>
          )}
          <div className="section">
            <button
              type="button"
              onClick={reveal(setIsLinkSafe)}
              disabled={isLinkSafe}
            >
              Is the email link safe?
            </button>
          </div>
          {isLinkSafe && (
            <>
              <div className="section">
                When you click on the link, you may get a warning that the link
                could be malicious.
              </div>
              <div className="section">
                How to tell if the link is safe:
              </div>
              <div className="section">
                <ul>
                  <li>You just requested it (e.g. by clicking "NEXT" above).</li>
                  <li>
                    In this case, the email should come from
                    noreply@volunteer-rep.firebaseapp.com
                  </li>
                </ul>
              </div>
              <div className="section">
                Generally you should avoid clicking links in email <i>that you
                did not actually request</i>.
                Your email program does not know that you requested the link
                so it is being justifiably cautious.
              </div>
            </>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export const LoginPagePw: React.FC<{}> = () => {
  useEffect(() => attachAuthUI(false));
  return (
    <div className="content">
      <div>
        <h1>Volunteer Reporting Login</h1>
      </div>

      <div id="firebaseui-auth-container" />

      <div className="explanation">
        <div className="section">
          <h3>Notice</h3>
        </div>
        <div className="section">
          Use this page to log in only if you followed the directions on{" "}
          <Link to={URLS.login()}>the normal login page</Link>, got stuck, sent
          the email described at the bottom of that page, and received
          instructions to log in here.
        </div>
      </div>
    </div>
  );
};

function attachAuthUI(link: boolean) {
  getApi().attachAuthUI("#firebaseui-auth-container", URLS.index(), link);
}
